/**
 * Helper: dataLayer (GTM / GA4 ecommerce)
 * -----------------------------------------------------------------------------
 * Pushes GA4-oriented ecommerce payloads for Google Tag Manager.
 * - Tags in GTM should listen for these event names and map to GA4 tags.
 * - Respects theme settings: `cnvs.settings.gtm`.
 *
 */
/**
 * Whether storefront ecommerce pushes are enabled.
 * @returns {Boolean}
 */
function isEnabled() {
  return Boolean(
    typeof window !== 'undefined' &&
    window.cnvs &&
    !window.cnvs.storybook &&
    window.cnvs.settings?.gtm?.enable &&
    window.cnvs.settings?.gtm?.enableStorefrontEcommerce,
  )
}

/**
 * @returns {String}
 */
function currencyCode() {
  return window.cnvs?.store?.currency || 'USD'
}

/**
 * Push raw object to dataLayer.
 * @param {Object} payload - dataLayer object.
 */
function push(payload) {
  if (!isEnabled()) {
    return
  }

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push(payload)
}

/**
 * @param {Number} cents - Shopify money in minor units.
 * @returns {Number}
 */
function fromCents(cents) {
  const num = Number(cents)
  return Number.isFinite(num) ? Math.round(num) / 100 : 0
}

/**
 * Build GA4 item from Theme /cart.js line item.
 * @param {Object} line - Cart line item.
 * @param {Number} [quantity] - Override quantity (e.g. amount just added).
 * @returns {Object}
 */
export function ga4ItemFromCartLine(line, quantity) {
  const qty = quantity ?? line.quantity
  const variantId = line.variant_id ?? line.id

  return {
    item_id: String(variantId),
    item_name: line.product_title || line.title,
    item_variant: line.product_has_only_default_variant ? null : line.variant_title || null,
    price: fromCents(line.final_price ?? line.price),
    quantity: qty,
  }
}

/**
 * Build GA4 item from formatted product + variant (Storefront / PDP).
 * @param {Object} product - Formatted product.
 * @param {Object} variant - Formatted variant.
 * @param {Number} quantity - Quantity.
 * @returns {Object}
 */
export function ga4ItemFromProductVariant(product, variant, quantity = 1) {
  const price = fromCents(variant?.price?.amount ?? 0)

  return {
    item_id: String(variant.id),
    item_name: product.title,
    item_variant: product.hasOnlyDefaultVariant ? null : variant.title,
    item_brand: product.vendor || null,
    item_category: product.type || null,
    price,
    quantity,
  }
}

/**
 * Clear previous ecommerce object (GA4 best practice).
 */
function clearEcommerce() {
  push({ ecommerce: null })
}

/**
 * @param {Object} product - Formatted product.
 * @param {Object} variant - Formatted variant.
 * @param {Number} [quantity=1] - Quantity selected.
 */
export function pushViewItem({ product, variant, quantity = 1 }) {
  if (!isEnabled() || !product || !variant) {
    return
  }

  clearEcommerce()
  push({
    event: 'view_item',
    ecommerce: {
      currency: currencyCode(),
      value: (parseFloat(variant.price?.amount) || 0) * quantity,
      items: [ga4ItemFromProductVariant(product, variant, quantity)],
    },
  })
}

/**
 * After /cart/add.js — uses returned cart and requested variant ids.
 * @param {Array<{ id: Number, quantity: Number }>} requestedItems
 * @param {Object} cart - Cart JSON returned by Shopify.
 */
export function pushAddToCartFromCartResponse(requestedItems, cart) {
  if (!isEnabled() || !cart?.items?.length || !requestedItems?.length) {
    return
  }

  const items = []

  requestedItems.forEach((req) => {
    const variantId = req.id
    const line = cart.items.find((row) => {
      return row.variant_id === variantId || row.id === variantId
    })

    if (line) {
      items.push(ga4ItemFromCartLine(line, req.quantity))
    }
  })

  if (!items.length) {
    return
  }

  const value = items.reduce((sum, row) => {
    return sum + (row.price * row.quantity)
  }, 0)

  clearEcommerce()
  push({
    event: 'add_to_cart',
    ecommerce: {
      currency: cart.currency || currencyCode(),
      value,
      items,
    },
  })
}

/**
 * @param {Object} cart - Vuex cart.response.
 */
export function pushViewCart(cart) {
  if (!isEnabled() || !cart?.items?.length) {
    return
  }

  const items = cart.items.map((line) => ga4ItemFromCartLine(line))

  clearEcommerce()
  push({
    event: 'view_cart',
    ecommerce: {
      currency: cart.currency || currencyCode(),
      value: fromCents(cart.total_price),
      items,
    },
  })
}

/**
 * @param {Object} cart - Vuex cart.response.
 */
export function pushBeginCheckout(cart) {
  if (!isEnabled() || !cart?.items?.length) {
    return
  }

  const items = cart.items.map((line) => ga4ItemFromCartLine(line))

  clearEcommerce()
  push({
    event: 'begin_checkout',
    ecommerce: {
      currency: cart.currency || currencyCode(),
      value: fromCents(cart.total_price),
      items,
    },
  })
}
